// auditpm-v7
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseKey)

// ─────────────────────────────────────────────────────────────────────────────
// MASTER SCOPE REGISTRY
// Single source of truth for all audit types / scopes across the system.
// Every scope has:
//   id          – stable internal code used for matching (never changes)
//   name        – canonical display name stored in DB
//   category    – high-level bucket (BIZSAFE | SHMS | ISO)
//   aliases     – any legacy or variant strings that map to this scope
// ─────────────────────────────────────────────────────────────────────────────
export const SCOPE_REGISTRY = [
  {
    id: 'S001',
    name: 'bizSAFE Risk Management Audit',
    category: 'BIZSAFE',
    aliases: [
      'bizsafe risk management audit',
      'bizsafe wsh rm audit',
      'bizsafe wshrm',
      'bizsafe rm audit',
      'bizsafe level 3',
      'wsh risk management audit',
      'wsh rm audit',
      'risk management audit',
    ],
  },
  {
    id: 'S002',
    name: 'Construction Worksite',
    category: 'SHMS',
    aliases: ['construction worksite', 'construction', 'consass', 'constrct'],
  },
  {
    id: 'S003',
    name: 'Metalworking Industry',
    category: 'SHMS',
    aliases: ['metalworking industry', 'metalworking', 'metal working'],
  },
  {
    id: 'S004',
    name: 'Shipyard',
    category: 'SHMS',
    aliases: ['shipyard', 'marine', 'marine industries'],
  },
  {
    id: 'S005',
    name: 'Oil Refinery or Petrochemical Plant',
    category: 'SHMS',
    aliases: [
      'oil refinery or petrochemical plant',
      'oil refinery',
      'petrochemical',
      'oil refinery / petrochemical plant',
      'oil & chemical',
    ],
  },
  {
    id: 'S006',
    name: 'Semiconductor Wafer Fabrication Plant',
    category: 'SHMS',
    aliases: [
      'semiconductor wafer fabrication plant',
      'semiconductor',
      'wafer fabrication',
    ],
  },
  {
    id: 'S007',
    name: 'Chemical Manufacturing Plant',
    category: 'SHMS',
    aliases: ['chemical manufacturing plant', 'chemical manufacturing', 'chemical'],
  },
  {
    id: 'S008',
    name: 'Pharmaceutical Plant',
    category: 'SHMS',
    aliases: ['pharmaceutical plant', 'pharmaceutical', 'pharma'],
  },
  {
    id: 'S009',
    name: 'ISO Internal Audit',
    category: 'ISO',
    aliases: ['iso internal audit', 'iso', 'internal audit'],
  },
  // SHMS top-level — matches if job stores 'SHMS Audit' as the type
  // (the sub-scope is stored in job.scope separately)
  {
    id: 'S010',
    name: 'SHMS Audit',
    category: 'SHMS',
    aliases: ['shms audit', 'shms', 'safety & health management system'],
  },
]

// ─────────────────────────────────────────────────────────────────────────────
// LOOKUP HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Resolve any string → scope entry (by name or alias, case-insensitive)
export function resolveScope(str) {
  if (!str) return null
  const t = str.trim().toLowerCase()
  return SCOPE_REGISTRY.find(s =>
    s.name.toLowerCase() === t || s.aliases.includes(t)
  ) || null
}

// Resolve any string → scope ID (S001…S010), or null
export function resolveScopeId(str) {
  return resolveScope(str)?.id || null
}

// Resolve any string → category (BIZSAFE | SHMS | ISO), or null
export function resolveScopeCategory(str) {
  return resolveScope(str)?.category || null
}

// True when an audit-type string is a bizSAFE RM scheme — registry-aware, so legacy/variant labels
// (e.g. "bizSAFE WSH RM Audit") are recognised. Prefer this over `=== AUDIT_TYPES.BIZSAFE` for any
// logic that gates checklist binding / CT17 routing.
export const isBizSAFEType = (str) => resolveScopeCategory(str) === 'BIZSAFE'

// All canonical scope names (for dropdowns)
export const ALL_SCOPE_NAMES = SCOPE_REGISTRY.map(s => s.name)

// Scope names shown in the Auditors module (exclude S010 top-level SHMS)
export const AUDITOR_SCOPE_NAMES = SCOPE_REGISTRY
  .filter(s => s.id !== 'S010')
  .map(s => s.name)

// SHMS sub-scope names only
export const SHMS_SCOPES = SCOPE_REGISTRY
  .filter(s => s.category === 'SHMS' && s.id !== 'S010')
  .map(s => s.name)

// ─────────────────────────────────────────────────────────────────────────────
// ELIGIBILITY MATCHING
// ─────────────────────────────────────────────────────────────────────────────

// Does an auditor's approved_scopes array cover the given job audit type?
// Rules:
//   1. Resolve jobAuditType → category via SCOPE_REGISTRY (handles all aliases)
//   2. Resolve each approved scope → category
//   3. Match if any approved scope category === job category
//   4. ISO jobs: always match (any approved auditor)
//   5. Empty approved_scopes: show auditor, warn at send time (don't silently block)
export function auditorMatchesJobType(approvedScopes, jobAuditType) {
  const jobEntry = resolveScope(jobAuditType)
  if (!jobEntry) return true                              // unknown type — don't block
  if (!approvedScopes || approvedScopes.length === 0) return true  // no scopes set — fallback
  if (jobEntry.category === 'ISO') return true            // ISO: any auditor

  return approvedScopes.some(scope => {
    const scopeEntry = resolveScope(scope)
    return scopeEntry && scopeEntry.category === jobEntry.category
  })
}

// Stronger check used at send-plan time: matches by scope ID, not just category.
// For BIZSAFE: auditor must have S001 in scopes.
// For SHMS: auditor must have at least one SHMS sub-scope (S002–S008, S010).
// Returns { pass: bool, reason: string }
export function auditScopeCheck(approvedScopes, jobAuditType) {
  const jobEntry = resolveScope(jobAuditType)
  if (!jobEntry) return { pass: true, reason: 'Scope not in registry — check skipped' }
  if (jobEntry.category === 'ISO') return { pass: true, reason: 'ISO — any auditor' }

  if (!approvedScopes || approvedScopes.length === 0) {
    return { pass: false, reason: 'Auditor has no approved scopes set' }
  }

  const approvedIds = approvedScopes.map(resolveScopeId).filter(Boolean)

  if (jobEntry.category === 'BIZSAFE') {
    const ok = approvedIds.includes('S001')
    return { pass: ok, reason: ok ? 'Approved for bizSAFE RM' : 'Auditor not approved for bizSAFE Risk Management Audit' }
  }

  if (jobEntry.category === 'SHMS') {
    const shmsIds = ['S002','S003','S004','S005','S006','S007','S008','S010']
    const ok = approvedIds.some(id => shmsIds.includes(id))
    return { pass: ok, reason: ok ? 'Approved for SHMS scope' : 'Auditor not approved for any SHMS scope' }
  }

  return { pass: true, reason: 'Match' }
}

// ─────────────────────────────────────────────────────────────────────────────
// LEGACY EXPORTS (kept for backward compat — redirect to registry)
// ─────────────────────────────────────────────────────────────────────────────

export const AUDIT_TYPES = {
  BIZSAFE: 'bizSAFE Risk Management Audit',
  SHMS: 'SHMS Audit',
  ISO: 'ISO Internal Audit',
}

export const INDUSTRIES = {
  BIZSAFE: [
    'Construction',
    'Metalworking',
    'Shipyard',
    'Oil refinery / Petrochemical plant',
    'Semiconductor wafer fabrication plant',
    'Chemical manufacturing plant',
    'Pharmaceutical plant',
    'Bulk storage terminal',
    'Other - High complexity',
    'Other - Medium complexity',
    'Other - Low complexity',
  ],
}

export const JOB_STATUSES = {
  ENQUIRY: 'Enquiry',
  QUESTIONNAIRE_SENT: 'Questionnaire sent',
  QUESTIONNAIRE_RECEIVED: 'Questionnaire received',
  QUOTATION_DRAFT: 'Quotation draft',
  QUOTATION_SENT: 'Quotation sent',
  CONFIRMED: 'Confirmed',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

export const AUDITOR_GRADES = {
  LEAD: 'Lead Auditor',
  AUDITOR: 'Auditor',
}

// Legacy functions — now backed by registry
export const AUDIT_CATEGORY = { BIZSAFE: 'BIZSAFE', SHMS: 'SHMS', ISO: 'ISO' }
export function getAuditCategory(str) { return resolveScopeCategory(str) }
export function getScopeCategory(str) { return resolveScopeCategory(str) }